import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["state", "accessibleWhenDisabled", "getItem"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useCallback } from 'react';
import { useId, useEvent } from 'ariakit-utils/hooks';
import { useStore, createMemoComponent } from 'ariakit-utils/store';
import { createHook, createElement } from 'ariakit-utils/system';
import { useCompositeItem } from '../composite/composite-item.js';
import { T as TabContext } from '../__utils-73a31bac.js';

function getPanelId(panels, id) {
  var _panels$items$find;

  if (!id) return;
  return panels == null ? void 0 : (_panels$items$find = panels.items.find(function (panel) {
    return panel.tabId === id;
  })) == null ? void 0 : _panels$items$find.id;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a tab element. The underlying element must be
 * wrapped in a `TabList` component or a component that implements the
 * `useTabList` props.
 * @see https://ariakit.org/components/tab
 * @example
 * ```jsx
 * const state = useTabState();
 * const props = useTab({ state });
 * <TabList state={state}>
 *   <Role {...props}>Tab 1</Role>
 * </TabList>
 * <TabPanel state={state}>Panel 1</TabPanel>
 * ```
 */


var useTab = createHook(function (_ref) {
  var _state2, _state3;

  var state = _ref.state,
      _ref$accessibleWhenDi = _ref.accessibleWhenDisabled,
      accessibleWhenDisabled = _ref$accessibleWhenDi === void 0 ? true : _ref$accessibleWhenDi,
      getItemProp = _ref.getItem,
      props = _objectWithoutProperties(_ref, _excluded); // Keep a reference to the default id so we can wait before all tabs have
  // been assigned an id before registering them in the state. See
  // https://github.com/ariakit/ariakit/issues/1721


  var defaultId = useId();
  var id = props.id || defaultId;
  state = useStore(state || TabContext, [useCallback(function (s) {
    return id && s.selectedId === id;
  }, [id]), "panels", "setSelectedId"]);
  var dimmed = props.disabled;
  var getItem = useCallback(function (item) {
    var nextItem = _objectSpread(_objectSpread({}, item), {}, {
      dimmed: dimmed
    });

    if (getItemProp) {
      return getItemProp(nextItem);
    }

    return nextItem;
  }, [dimmed, getItemProp]);
  var onClickProp = props.onClick;
  var onClick = useEvent(function (event) {
    var _state;

    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    (_state = state) == null ? void 0 : _state.setSelectedId(id);
  });
  var panelId = getPanelId((_state2 = state) == null ? void 0 : _state2.panels, id);
  props = _objectSpread(_objectSpread({
    id: id,
    role: "tab",
    "aria-selected": !!id && ((_state3 = state) == null ? void 0 : _state3.selectedId) === id,
    "aria-controls": panelId || undefined
  }, props), {}, {
    onClick: onClick
  });
  props = useCompositeItem(_objectSpread(_objectSpread({
    state: state
  }, props), {}, {
    accessibleWhenDisabled: accessibleWhenDisabled,
    getItem: getItem,
    shouldRegisterItem: !!defaultId ? props.shouldRegisterItem : false
  }));
  return props;
});
/**
 * A component that renders a tab element. The underlying element must be
 * wrapped in a `TabList` component.
 * @see https://ariakit.org/components/tab
 * @example
 * ```jsx
 * const tab = useTabState();
 * <TabList state={tab}>
 *   <Tab>Tab 1</Tab>
 *   <Tab>Tab 2</Tab>
 * </TabList>
 * <TabPanel state={tab}>Panel 1</TabPanel>
 * <TabPanel state={tab}>Panel 2</TabPanel>
 * ```
 */

var Tab = createMemoComponent(function (props) {
  var htmlProps = useTab(props);
  return createElement("button", htmlProps);
});
export { Tab, useTab };